import React, { useEffect, useRef } from 'react';

import { SidePanel } from '@flipdish/ui-library/components/organisms/SidePanel';

import { BrandSelectorMenuContent } from '../../../../components/RMS/BrandSelector/components/BrandSelectorMenuContent';
import { useBrandSelector } from '../../../../components/RMS/BrandSelector/hooks/useBrandSelector';

type Props = {
  /** Heading and accessible name of the panel: the organisation the brands belong to. */
  title: string;
  /** Closes the panel. */
  onClose: () => void;
  /** Swaps the panel to the organisation switcher. */
  onSwitchOrg: () => void;
};

/** The brand selector's list as `SidePanel` content, opened from the header avatar. */
export const OrganisationPanel = ({ title, onClose, onSwitchOrg }: Props) => {
  const brandSelector = useBrandSelector({ enabled: true, onClose, onSwitchOrg });
  const {
    currentApp,
    currentOrg,
    orgId,
    brands,
    filteredBrands,
    loading,
    selectedIndex,
    maxVisibleItems,
    handleSearch,
    handleChange,
    handleKeyDown,
  } = brandSelector;
  const listRef = useRef<HTMLDivElement>(null);
  const showsSearch = brands.length > maxVisibleItems;

  // Arrow keys and Enter are handled on this container, so it needs focus unless the search field took it.
  useEffect(() => {
    if (!loading && !showsSearch) {
      listRef.current?.focus();
    }
  }, [loading, showsSearch]);

  return (
    <>
      <SidePanel.Header>
        <h2 className="truncate pr-14 text-lg font-semibold text-primary">{title}</h2>
      </SidePanel.Header>
      <SidePanel.Content className="px-0 lg:px-0" data-fd="organisation-panel">
        {/* MUI MenuItems need a menu parent; this wrapper also takes focus for the arrow-key handling. */}
        <div
          ref={listRef}
          tabIndex={-1}
          onKeyDown={handleKeyDown}
          className="outline-none"
          role="menu"
          aria-label={title}
        >
          <BrandSelectorMenuContent
            currentApp={currentApp}
            orgId={currentOrg?.orgId || orgId}
            brands={brands}
            filteredBrands={filteredBrands}
            loading={loading}
            selectedIndex={selectedIndex}
            maxVisibleItems={maxVisibleItems}
            onSearch={handleSearch}
            onSelect={handleChange}
          />
        </div>
      </SidePanel.Content>
    </>
  );
};

export default OrganisationPanel;
